import { useState } from "react";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiList,
  MiMenu,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { IAttributesResponse } from "@miplatform/types";
import {
  mdiHexagonOutline,
  mdiPencilOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { useAttributes, usePermissions } from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { SectionInfo } from "../../components/SectionInfo/SectionInfo";

export function Attributes() {
  const [selectedAttribute, setSelectedAttribute] =
    useState<IAttributesResponse | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [search, setSearch] = useState("");

  const { navigate } = useRouter();
  const theme = useTheme();
  const debouncedSearch = useDebounce(search);
  const { hasPermission } = usePermissions();

  const canCreateAttribute = hasPermission(
    PERMISSIONS.MATERIAL_ATTRIBUTE.CREATE
  );
  const canUpdateAttribute = hasPermission(
    PERMISSIONS.MATERIAL_ATTRIBUTE.UPDATE
  );
  const canDeleteAttribute = hasPermission(
    PERMISSIONS.MATERIAL_ATTRIBUTE.DELETE
  );

  const {
    attributes,
    recentAttributes,
    refresh,
    isLoading,
    getAttributes,
    getRecentAttributes,
    deleteAttribute,
  } = useAttributes();

  const sections = [
    {
      fieldset: [
        {
          renderValue: (data: IAttributesResponse) => (
            <SectionInfo data={data} />
          ),
        },
        {
          label: "Options",
          field: "attributeOptions",
          renderValue: (data: IAttributesResponse) =>
            data.attributeOptions?.map((o) => o.value).join(", ") || "--",
          style: { maxWidth: 500 },
        },
        {
          label: "Attached Categories",
          field: "categories",
          renderValue: (data: IAttributesResponse) =>
            data.categories?.map((o) => o.name).join(", ") || "--",
        },
      ],
    },
  ];

  const handleNavigate = (item: IAttributesResponse) => {
    if (item.id) {
      navigate(`/estimating/configuration/attributes/${item.id}`);
    }
  };

  const renderItemActions = (item: IAttributesResponse) => {
    const actions = [];

    if (canUpdateAttribute) {
      actions.push({
        label: BUTTON_LABELS.EDIT,
        icon: mdiPencilOutline,
        action: () => handleNavigate(item),
      });
    }

    if (canDeleteAttribute) {
      actions.push({
        label: BUTTON_LABELS.DELETE,
        icon: mdiTrashCanOutline,
        action: () => handleOpenDeleteModal(item),
        color: theme.palette.red.main,
      });
    }
    return actions.length > 0 ? (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu data={actions} />
      </Box>
    ) : null;
  };

  const handleOpenDeleteModal = (item: IAttributesResponse) => {
    setSelectedAttribute(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedAttribute) {
      deleteAttribute(selectedAttribute);
      setOpenDeleteModal(false);
    }
  };

  return (
    <>
      <MiBuildPageHeader
        title="Attributes"
        loading={isLoading}
        titleIcon={
          <MiIcon path={mdiHexagonOutline} color={theme.palette.primary.main} />
        }
        actionComponent={
          canCreateAttribute && (
            <MiButton
              title="Attribute"
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{ path: mdiPlus }}
              onClick={() =>
                navigate("/estimating/configuration/add-attributes", {
                  state: { viewOnly: false },
                })
              }
              data-testid="add-attribute-btn"
            />
          )
        }
        search={search}
        onHandleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
        searchPlaceholder="Search"
        actionContainerStyle={{
          flexWrap: "nowrap",
        }}
      />
      <MiBox>
        <MiList
          viewType="card"
          cardProps={{
            sections,
            renderItemActions,
            onClick: handleNavigate,
            leftCardProps: { fieldset: [] },
          }}
          loading={isLoading}
          search={debouncedSearch}
          data={attributes}
          dataProvider={getAttributes}
          recentData={recentAttributes}
          recentDataProvider={getRecentAttributes}
          refresh={refresh}
          paginationMode={PAGINATION.SERVER}
          emptyListProps={{
            text: "No Attributes found",
          }}
          data-testid="attributes-list"
        />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          open={openDeleteModal}
          textPositive={BUTTON_LABELS.DELETE}
          buttonPositiveColor={BUTTON_COLORS.RED}
          titleIcon={{
            path: mdiTrashCanOutline,
            color: theme.palette.red.main,
          }}
          handleNegative={() => setOpenDeleteModal(false)}
          handlePositive={handleConfirmDeleteModal}
          description={`You are about to delete ${selectedAttribute?.name}. Are you sure you would like to continue?`}
        />
      </MiBox>
    </>
  );
}
